import { useEffect, useState } from "react";
import { Star, Loader2, CheckCircle2, PenLine } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import api from "@/lib/api";
import { StarRating } from "@/components/StarRating";

const fmtDate = (iso) =>
  new Date(iso).toLocaleDateString("ro-RO", { day: "numeric", month: "long", year: "numeric" });

export const ReviewsSection = ({ productId }) => {
  const [data, setData] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ author: "", rating: 5, comment: "" });
  const [hover, setHover] = useState(0);

  const load = () => api.get(`/products/${productId}/reviews`).then(({ data }) => setData(data)).catch(() => {});

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [productId]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.comment.trim()) { toast.error("Scrie un comentariu"); return; }
    setSubmitting(true);
    try {
      await api.post(`/products/${productId}/reviews`, form);
      toast.success("Mulțumim pentru recenzie!");
      setForm({ author: "", rating: 5, comment: "" });
      setShowForm(false);
      load();
    } catch {
      toast.error("Nu am putut trimite recenzia. Încearcă din nou.");
    } finally {
      setSubmitting(false);
    }
  };

  if (!data) {
    return <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;
  }

  const maxDist = Math.max(1, ...Object.values(data.distribution));

  return (
    <section className="mt-14 border-t border-slate-200 pt-10" data-testid="reviews-section">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <h2 className="font-heading text-2xl font-bold text-slate-900">
          Recenzii clienți {data.count > 0 && <span className="text-slate-400">({data.count})</span>}
        </h2>
        <Button variant="outline" className="rounded-full" onClick={() => setShowForm((s) => !s)} data-testid="write-review-toggle">
          <PenLine className="mr-2 h-4 w-4" /> Scrie o recenzie
        </Button>
      </div>

      {data.count > 0 && (
        <div className="mt-6 grid gap-6 rounded-2xl border border-slate-200 bg-slate-50 p-6 sm:grid-cols-[auto_1fr] sm:items-center">
          <div className="text-center sm:pr-8 sm:text-left">
            <p className="font-heading text-5xl font-extrabold text-slate-900" data-testid="reviews-average">{data.average}</p>
            <StarRating value={data.average} size={18} className="mt-1 justify-center sm:justify-start" />
            <p className="mt-1 text-sm text-slate-500">{data.count} recenzii</p>
          </div>
          <div className="space-y-1.5">
            {[5, 4, 3, 2, 1].map((star) => (
              <div key={star} className="flex items-center gap-2 text-sm">
                <span className="flex w-8 items-center gap-0.5 text-slate-500">{star}<Star className="h-3 w-3" fill="#f59e0b" stroke="#f59e0b" /></span>
                <div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200">
                  <div className="h-full rounded-full bg-amber-400" style={{ width: `${(data.distribution[star] / maxDist) * 100}%` }} />
                </div>
                <span className="w-6 text-right text-slate-400">{data.distribution[star]}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {showForm && (
        <form onSubmit={submit} className="mt-6 space-y-4 rounded-2xl border border-slate-200 p-6" data-testid="review-form">
          <div>
            <Label className="text-sm">Nota ta</Label>
            <div className="mt-1.5 flex gap-1" data-testid="review-rating-stars">
              {[1, 2, 3, 4, 5].map((i) => (
                <button type="button" key={i} onClick={() => setForm((f) => ({ ...f, rating: i }))}
                  onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(0)}
                  data-testid={`review-star-${i}`}>
                  <Star className="h-7 w-7 transition-transform hover:scale-110"
                    fill={i <= (hover || form.rating) ? "#f59e0b" : "none"}
                    stroke={i <= (hover || form.rating) ? "#f59e0b" : "#cbd5e1"} />
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label htmlFor="rv-author" className="text-sm">Numele tău</Label>
            <Input id="rv-author" value={form.author} onChange={(e) => setForm((f) => ({ ...f, author: e.target.value }))}
              placeholder="ex. Maria P." className="mt-1.5" data-testid="review-author" />
          </div>
          <div>
            <Label htmlFor="rv-comment" className="text-sm">Recenzia ta</Label>
            <Textarea id="rv-comment" value={form.comment} onChange={(e) => setForm((f) => ({ ...f, comment: e.target.value }))}
              placeholder="Cum ți s-a părut produsul?" rows={4} className="mt-1.5" data-testid="review-comment" required />
          </div>
          <Button type="submit" disabled={submitting} className="rounded-full" data-testid="review-submit">
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Trimite recenzia
          </Button>
        </form>
      )}

      <div className="mt-8 space-y-5" data-testid="reviews-list">
        {data.reviews.length === 0 && (
          <p className="rounded-xl border border-dashed border-slate-300 py-10 text-center text-slate-500">
            Încă nu există recenzii. Fii primul care lasă o părere!
          </p>
        )}
        {data.reviews.map((r) => (
          <div key={r.id} className="rounded-xl border border-slate-200 p-5" data-testid={`review-${r.id}`}>
            <div className="flex items-start gap-3">
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary/10 font-heading font-bold text-primary">
                {r.author.charAt(0).toUpperCase()}
              </span>
              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-medium text-slate-900">{r.author}</span>
                  {r.verified && (
                    <span className="flex items-center gap-1 text-xs font-medium text-emerald-600">
                      <CheckCircle2 className="h-3.5 w-3.5" /> Cumpărător verificat
                    </span>
                  )}
                </div>
                <div className="mt-1 flex items-center gap-2">
                  <StarRating value={r.rating} size={14} />
                  <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                </div>
                <p className="mt-2 leading-relaxed text-slate-700">{r.comment}</p>
                {r.video_url && (
                  <video
                    className="mt-3 w-full max-w-[280px] rounded-xl border border-slate-200 bg-black"
                    muted
                    autoPlay
                    loop
                    playsInline
                    controls
                    preload="auto"
                    data-testid={`review-video-${r.id}`}
                  >
                    {r.video_url_webm && <source src={r.video_url_webm} type="video/webm" />}
                    <source src={r.video_url} type="video/mp4" />
                  </video>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};
